import React, { useEffect, useState, useRef, useCallback } from "react";
import { cqAPI, getBid, Auth } from "../../lib/Auth";
import DOMPurify from "dompurify";
import {
  faChevronDown,
  faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./Inbox.css";
import extractUrls from "extract-urls";
import {
  Flex,
  Box,
  Text,
  Icon,
  Tooltip,
  Button,
  IconButton,
  Link,
  VStack,
  HStack,
  useToast,
  Input,
  useColorMode,
  Image,
  Spinner
} from "@chakra-ui/react";
import {
  ChatIcon,
  CheckCircleIcon,
  AtSignIcon,
  AttachmentIcon,
  EmailIcon,
  ArrowForwardIcon,
  PhoneIcon,
  DeleteIcon,
  ExternalLinkIcon,
  CloseIcon
} from "@chakra-ui/icons";
import moment from "moment";

import { useAppContext } from "../../AppContext";

const _ = require("lodash");

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const hyperlinkUrls = (text) => {
  if (!text) return "";
  if (/<a\b/i.test(text)) return text;

  let urls = extractUrls(text) || [];
  urls = Array.from(new Set(urls)).sort((a,b) => b.length - a.length);
  let out = text;
  const placeholders = new Map();

  urls.forEach((url, i) => {
    const key = `__URL__${i}`;

    const normalized = url.startsWith("http") ? url : `https://${url}`;
    let isExternal = true;
    try {
      const host = new URL(normalized).host.toLowerCase();
      isExternal = host !== window.location.host && !host.includes("calendly");
    } catch {
      isExternal = true;
    }
    const hyperlink = `<a href="${normalized}" ${isExternal ? 'data-external-link="true"' : ""} target="_blank" rel="noopener noreferrer" style="color:#E274FF; text-decoration:underline;">${url}</a>`;
    placeholders.set(key, hyperlink);
    const rawEsc = escapeRe(url);

    out = out.replace(new RegExp(`${rawEsc}\\.(?=\\s|$)`, "g"), `${key}.`);
    out = out.replace(new RegExp(`${rawEsc}(?!\\.)`, "g"), key);
  });
  placeholders.forEach((anchor, key) => {
    out = out.replaceAll(key, anchor);
  });

  return out;
}

const sanitizeHtml = (message) => {
  const withMentions = (message.mentions || []).reduce(
    (acc, cur) => acc.replace(RegExp(cur.name, "ig"), `<b>${cur.name}</b>`),
    message.body
  );
  const withHyperlinks = hyperlinkUrls(withMentions);
  return DOMPurify.sanitize(withHyperlinks, { ADD_ATTR: ["data-external-link"] });
}

function InboxMessages() {
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const bottomOfList = useRef(null);
  const [userInfo, setUserInfo] = useState({});
  const textareaRef = useRef(null);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const [widgetCollapsed, setWidgetCollapsed] = useState(true);
  const [messageType, setMessageType] = useState([]);
  const [replyType] = useState("reply");
  const toast = useToast();
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [placeHolderVisible, setPlaceHolderVisible] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const { colorMode } = useColorMode();
  const { realtime, realtimeInitialized, location } = useAppContext();
  const typingTimer = useRef(null);

  // --- Location selector related state (only used when URL param says so) ---
  const [locations, setLocations] = useState([]);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [locationsReady, setLocationsReady] = useState(false); // only relevant if selector is enabled
  // Track if a location has been chosen (from URL or in-session without reload)
  const getUrlParams = (key) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    return newSearchParams.get(key);
  };
  const [locationChosen, setLocationChosen] = useState(
    getUrlParams("location_selected") === "1"
  );
  // -------------------------------------------------------------------------

  const setUrlParam = (url, key, value) => {
    if (value === undefined || value === null) return;
    url.searchParams.set(key, value);
  };

  const replaceOnlyBidAndMarkSelected = (newBid) => {
    const url = new URL(window.location.href);
    setUrlParam(url, "bid", newBid);
    setUrlParam(url, "location_selected", "1");
    window.location.replace(url.toString());
  };

  useEffect(() => {
    const scrollToBottom = () => {
      bottomOfList.current?.scrollIntoView({ behavior: "smooth" });
    };
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const processUseAuth = (response) => {
    if (response.type === "ok") {
      return response.data.user;
    } else {
      return null;
    }
  };

  useEffect(() => {
    getCurrentAuthenticatedUser(Auth.user)
      .then(processUseAuth)
      .then((u) => {
        if ((Auth.user && !u) || (!Auth.user && u)) {
          Auth.storeUser(u);
        }
        if (u) {
          Auth.login(u);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  });

  const getCurrentAuthenticatedUser = async () => {
    try {
      return Auth.currentAuthenticatedUser();
    } catch (error) {}
  };

  const fetchAllMessages = async () => {
    const response = await cqAPI.get(`/messages`);
    setMessages(response.data.conversations);
    setUserInfo(response.data.userInfo);

    const types = [];
    if (response.data.userInfo?.possible_notification_pref?.indexOf("email") !== -1) {
      types.push("email");
    }
    if (response.data.userInfo?.possible_notification_pref?.indexOf("sms") !== -1) {
      types.push("sms");
    }
    setMessageType(types);
  };

  const debouncedTyping = useCallback(
    () =>
      _.debounce(async () => {
        await cqAPI.post("/chat/typing", {
          bid: getBid(),
          target_uid: userInfo.user_id,
          is_internal_note: replyType === "internalNote",
        });
      }, 3000),
    [userInfo, replyType]
  );

  const showTyping = useCallback(
    (payload) => {
      if (payload.data.target_uid !== Auth.user.id) {
        return;
      }
      if (payload.data.uid === Auth.user.id) {
        return;
      }
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
      setTyping(true);
      typingTimer.current = setTimeout(() => {
        setTyping(false);
        typingTimer.current = null;
      }, 5000);
    },
    [typingTimer]
  );

  const toggleWidgetStatus = async () => {
    window.parent.postMessage(
      {
        action: { collapsed: !widgetCollapsed },
      },
      "*"
    );
    setWidgetCollapsed(!widgetCollapsed);
  };

  // -------- LOCATION SELECTOR: only if ?show_location_selector=1 -----------
  const selectorEnabled = getUrlParams("show_location_selector") === "1";

  // Fetch locations list if selector is enabled, otherwise mark ready immediately
  useEffect(() => {
    if (!selectorEnabled) {
      setLocationsReady(true);
      return;
    }
    cqAPI
      .get("/locations")
      .then((response) => {
        const list = response?.data || [];
        setLocations(list);
        const alreadySelected = getUrlParams("location_selected") === "1";
        setShowLocationSelector(list.length > 1 && !alreadySelected);
      })
      .finally(() => {
        setLocationsReady(true);
      });
  }, [selectorEnabled]);

  // Fetch messages normally unless selector is currently showing (only in selector flow)
  useEffect(() => {
    if (!Auth.user) return;
    if (selectorEnabled && showLocationSelector) return; // wait until user selects
    fetchAllMessages();
  }, [selectorEnabled, showLocationSelector]);

  // Realtime subscriptions (avoid while selector visible)
  useEffect(() => {
    if (!realtime.current) return;
    if (selectorEnabled && showLocationSelector) return;

    const UpdateTopics = [
      `conv_status_change/${getBid()}/${Auth.user.id}`,
      `_new_chat_message/${getBid()}/${Auth.user.id}`,
    ];
    UpdateTopics.forEach((topic) =>
      realtime.current.subscribeToTopic(topic, getBid(), fetchAllMessages)
    );
    realtime.current.subscribeToTopic(
      `chat_typing/${getBid()}/${Auth.user.id}`,
      getBid(),
      showTyping
    );

    return () => {
      UpdateTopics.forEach((topic) =>
        realtime.current.unsubscribeFromTopic(topic, getBid(), fetchAllMessages)
      );
      realtime.current.unsubscribeToTopic(
        `chat_typing/${getBid()}/${Auth.user.id}`,
        getBid(),
        showTyping
      );
    };
  }, [realtimeInitialized, realtime, showTyping, selectorEnabled, showLocationSelector]);

  // Cleanup on route change/unmount
  useEffect(() => {
    const current = realtime.current;
    return () => {
      const UpdateTopics = [
        `conv_status_change/${getBid()}/${Auth.user.id}`,
        `_new_chat_message/${getBid()}/${Auth.user.id}`,
      ];
      UpdateTopics.forEach((topic) =>
        current.unsubscribeFromTopic(topic, getBid(), fetchAllMessages)
      );
      current.unsubscribeToTopic(
        `chat_typing/${getBid()}/${Auth.user.id}`,
        getBid(),
        showTyping
      );
    };
  }, [location, realtime, showTyping]);
  // ------------------------------------------------------------------------

  const getMessageIconAndLabel = (type) => {
    let icons = [];
    switch (type) {
      case "call":
        icons.push({ icon: PhoneIcon, label: "Phone Call" });
        break;
      case "email":
        icons.push({ icon: EmailIcon, label: "Email" });
        break;
      case "sms":
        icons.push({ icon: ChatIcon, label: "SMS" });
        break;
      case "emailsms":
        icons.push({ icon: EmailIcon, label: "Email" });
        icons.push({ icon: ChatIcon, label: "SMS" });
        break;
      case "voicemail":
        icons.push({ icon: AtSignIcon, label: "Voicemail" });
        break;
      default:
        break;
    }
    return icons;
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    }
  };

  const handleFocus = () => {
    setPlaceHolderVisible(false);
  };

  const handleBlur = () => {
    if (textareaRef.current.innerText) {
      return;
    }
    setPlaceHolderVisible(true);
  };

  const handlePlaceHolderClick = () => {
    textareaRef.current.focus();
  };

  const handleSubmit = async () => {
    if (!textareaRef.current.innerText) return;
    setDisableSendButton(true);
    try {
      const response = await cqAPI.post("/messages", {
        body: textareaRef.current.innerText,
        to: userInfo.user_id,
        type: messageType.join(""),
        attachments: attachedFiles,
        related_to: userInfo.user_id,
      });
      if (response.type === "ok") {
        setAttachedFiles([]);
        textareaRef.current.innerText = "";
      } else if (response.msg) {
        throw new Error(response.msg || response.error);
      }
    } catch (error) {
      toast({
        title: "Error sending the message.",
        description: error.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setDisableSendButton(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    const uploadedFiles = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await cqAPI.post("/upload?private=1", formData);
        if (response.type === "ok") {
          uploadedFiles.push({
            name: file.name || "Attachment",
            url: response.url,
            mimetype: file.type,
          });
        } else {
          toast({
            title: "Error uploading file.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          break;
        }
      } catch (error) {
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        break;
      }
    }
    if (uploadedFiles.length > 0) {
      toast({
        title: "File(s) uploaded successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      setAttachedFiles((currentFiles) => [...currentFiles, ...uploadedFiles]);
    }
  };

  const handleDeleteAttachment = (index) => {
    setAttachedFiles((currentFiles) => currentFiles.filter((_, i) => i !== index));
  };

  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest("a[data-external-link]");
      if (link) {
        e.preventDefault();
        window.open(link, "_blank", "noopener, noreferrer");
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const AdecAIDisclaimer = () => {
    if (!showDisclaimer) return null;

    return (
      <Flex>
        <Box
          bg={colorMode === "light" ? "gray.200" : "gray.600"}
          p={3}
          borderTopWidth="1px"
          maxHeight="100px"
          width={"full"}
          position="relative"
        >
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" flex="1">
              By chatting with us you accept and agree to the{" "}
              <Link href="https://www.a-dec.com/legal/terms-of-use" textDecoration="underline" target="_blank">
                Terms of Use
              </Link>
              . A-dec AI assistant can make mistakes.
            </Text>
            <IconButton
              icon={<CloseIcon />}
              size="sm"
              variant="ghost"
              onClick={() => setShowDisclaimer(false)}
              aria-label="Close disclaimer"
              ml={2}
            />
          </Flex>
        </Box>
      </Flex>
    );
  };

  // Selection handler (only used if selector flow is enabled & visible)
  const handleLocationSelect = (loc) => {
    const currentBid = getUrlParams("bid");
    if (currentBid !== loc.bid) {
      replaceOnlyBidAndMarkSelected(loc.bid);
      // after reload, locationChosen will be true from URL param
    } else {
      // Same bid: no reload, just close selector and mark chosen in-session
      setShowLocationSelector(false);
      setLocationChosen(true);
    }
  };

  // Compute the selected location (from current bid) for the header bar
  const currentBid = getUrlParams("bid");
  const selectedLocation =
    selectorEnabled && locationsReady
      ? locations.find((l) => l.bid === currentBid)
      : null;

  // helper to render a compact address line
  const renderAddress = (loc) => {
    const addr = loc?.location || {};
    const line1 = addr.line1 || "";
    const line2 = addr.line2 ? addr.line2 : "";
    const cityState = [addr.city, addr.state].filter(Boolean).join(", ");
    const zip = addr.zip || "";
    return [line1, line2, cityState, zip].filter(Boolean).join(" · ");
  };

  return (
    <Flex direction="column" h="100vh" w="100%">
      {/* Selected Location Bar (appears after a location is chosen in selector flow) */}
      {selectorEnabled && locationsReady && locationChosen && selectedLocation && (
        <Box
          w="100%"
          bg={colorMode === "light" ? "purple.50" : "purple.900"}
          color={colorMode === "light" ? "purple.900" : "purple.100"}
          px={4}
          py={2}
          borderBottomWidth="1px"
        >
          <HStack justify="space-between" spacing={4}>
            <Text fontWeight="semibold">
              Location: <strong>{selectedLocation.name}</strong>
            </Text>
            <Link
              onClick={() => setShowLocationSelector(true)}
              textDecoration="underline"
              fontWeight="medium"
              cursor="pointer"
            >
              Change Location
            </Link>
          </HStack>
        </Box>
      )}

      {/* Blocker overlay: spinner while selector is enabled and locations not ready */}
      {selectorEnabled && !locationsReady && (
        <Flex
          position="fixed"
          inset="0"
          zIndex={3000}
          align="center"
          justify="center"
          bg={colorMode === "light" ? "white" : "gray.900"}
        >
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text color={colorMode === "light" ? "gray.600" : "gray.300"}>Loading…</Text>
          </VStack>
        </Flex>
      )}

      {/* Full-screen location selector overlay (only if enabled and more than 1 location) */}
      {selectorEnabled && locationsReady && showLocationSelector && (
        <Flex
          position="fixed"
          inset="0"
          zIndex={2000}
          bg={colorMode === "light" ? "white" : "gray.900"}
          align="center"
          justify="center"
          p={6}
        >
          <Box
            w="full"
            maxW="md"
            borderWidth="1px"
            borderRadius="xl"
            p={8}
            bg={colorMode === "light" ? "white" : "gray.800"}
            boxShadow="xl"
          >
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="2xl" fontWeight="bold" mb={1}>
                  Select the office
                </Text>
              </Box>

              <VStack spacing={3} align="stretch">
                {locations.map((loc) => (
                  <Button
                    key={loc.bid}
                    size="lg"
                    rightIcon={<ArrowForwardIcon />}
                    onClick={() => handleLocationSelect(loc)}
                    justifyContent="space-between"
                  >
                    {/* CHANGED: show name and small address under it */}
                    <Box textAlign="left">
                      <Text fontWeight="semibold">{loc.name}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {renderAddress(loc)}
                      </Text>
                    </Box>
                  </Button>
                ))}
              </VStack>
            </VStack>
          </Box>
        </Flex>
      )}

      <Box
        className="menu-header widget-title"
        zIndex="1399"
        boxShadow="sm"
        width="100%"
        bg={getUrlParams("bgcolor") || ""}
        p={2}
        position="relative"
        onClick={() => {
          toggleWidgetStatus();
        }}
        style={{ cursor: "pointer" }}
      >
        <Box textAlign="left" mb="5"></Box>
        <Text
          fontSize="md"
          fontWeight="bold"
          position="absolute"
          left="16px"
          top="50%"
          color={getUrlParams("color") || ""}
          transform="translateY(-50%)"
        >
          Have any questions? Ask us now!
        </Text>
        <Flex position="absolute" right="16px" top="50%" transform="translateY(-50%)">
          <FontAwesomeIcon icon={widgetCollapsed ? faChevronDown : faChevronUp} className="menu-chevron" />
        </Flex>
      </Box>

      {!widgetCollapsed && (
        <Flex direction="column" p={4} overflowY="auto" flex="1">
          <Flex direction="column" p={4}>
            <Flex direction="column" style={{ marginTop: "40px" }}>
              {messages.length === 0 ? (
                <Flex justifyContent="center" alignItems="center" className="empty-message-list" h="100%">
                  {disableSendButton && (
                    <div className="spinner" style={{ position: "absolute", bottom: "0px" }}></div>
                  )}
                </Flex>
              ) : (
                messages.map((message, index) => {
                  const isUserMessage =
                    message.author === userInfo.user_id ||
                    (message.type === "call" && message.direction === "incoming");
                  const seen = message?.emailStatus?.status?.opened ? true : false;
                  const bgColor =
                    message.direction === "internal_note"
                      ? "orange.100"
                      : isUserMessage
                      ? "green.500"
                      : "blue.800";

                  const messageColor = message.direction === "internal_note" ? "#000" : "#fff";
                  const footerColor = isUserMessage ? "gray.300" : "gray.400";

                  return (
                    <Flex
                      key={index}
                      direction="column"
                      align={isUserMessage ? "flex-start" : "flex-end"}
                      my={2}
                      color={messageColor}
                      className={isUserMessage ? "widget-user-message" : "widget-viva-message"}
                      id={`widget-message-${index}`}
                    >
                      <Box p={3} bg={bgColor} borderRadius="lg" position="relative" maxWidth="540px" minWidth="200px">
                        <Text fontSize="sm" align="left" dangerouslySetInnerHTML={{ __html: sanitizeHtml(message) }} />
                        {message.attachments && message.attachments.length > 0 && (
                          <Flex>
                            {message.attachments.map((attachment) => (
                              <Link
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadFile(attachment.url, attachment.name);
                                }}
                                href="#"
                                target="_blank"
                                key={attachment.url}
                              >
                                {attachment.name}
                                <ExternalLinkIcon ml="2" />
                              </Link>
                            ))}
                          </Flex>
                        )}
                        <Flex justify="space-between" align="center">
                          <Text fontSize="xs" color={footerColor} mt={2}>
                            {Auth?.user?.business?.partner && message.author_name === "Viva AI"
                              ? "A-dec AI"
                              : (message.author_name || "").replace(/[^a-zA-Z ]/g, "")}
                          </Text>
                          <Flex align="center">
                            {getMessageIconAndLabel(message.type).map(({ icon, label }, iconIndex) => (
                              <Tooltip key={iconIndex} label={label} hasArrow>
                                <Icon as={icon} color={footerColor} w={4} h={4} mr={2} />
                              </Tooltip>
                            ))}
                            {seen && (
                              <Tooltip label="Message was opened" hasArrow>
                                <Icon as={CheckCircleIcon} color={footerColor} w={4} h={4} />
                              </Tooltip>
                            )}
                          </Flex>
                        </Flex>
                        <Text fontSize="xs" color={footerColor} align="left">
                          {moment(message.created).format("MMM D, YYYY h:mm a")}
                        </Text>
                      </Box>
                    </Flex>
                  );
                })
              )}
              <div ref={bottomOfList} />
            </Flex>
          </Flex>
        </Flex>
      )}

      {/* Sticky Sending Area with Conditional Background */}
      {!widgetCollapsed && (
        <Box
          borderTopWidth="1px"
          bg={"transparent"}
          color={colorMode === "light" ? "#000000" : "#ffffff"}
        >
          <Flex direction="row" height="100%">
            {typing && (
              <Flex position="absolute" bottom="5px" left="10px">
                <Box className="throbbing-dot" mr={1}></Box>
                <Box className="throbbing-dot" mr={1} style={{ animationDelay: "0.3s" }}></Box>
                <Box className="throbbing-dot" style={{ animationDelay: "0.6s" }}></Box>
              </Flex>
            )}

            <Flex direction="column" flex="1">
              <VStack spacing={2} align="stretch">
                <HStack justifyContent="space-between" w="full">
                  <HStack position="relative">
                    <Flex position="absolute" top="2px">
                      <IconButton
                        icon={<AttachmentIcon />}
                        onClick={handleFileButtonClick}
                        isDisabled={disableSendButton}
                        variant="none"
                        style={{ filter: "saturate(0%) brightness(60%) contrast(90%)" }}
                        mr={2}
                      />
                      <Input ref={fileInputRef} type="file" onChange={(e) => handleFileUpload(e)} hidden />
                    </Flex>
                  </HStack>
                  {attachedFiles && (
                    <Flex justify="flex-end">
                      {attachedFiles.map((attachedFile, index) => (
                        <Flex key={index} position="relative" ml={index > 0 ? 2 : 0}>
                          <Tooltip label={attachedFile.name} hasArrow>
                            <IconButton icon={<AttachmentIcon />} variant="outline" size="md" />
                          </Tooltip>
                          <Box position="absolute" right="-8px" top="-8px">
                            <IconButton
                              icon={<DeleteIcon />}
                              size="xs"
                              onClick={() => handleDeleteAttachment(index)}
                              aria-label="Remove attachment"
                              variant="ghost"
                              style={{
                                filter:
                                  "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                              }}
                            />
                          </Box>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                  )
                </HStack>

                <Flex align="center" w="full" position="relative">
                  <div
                    contentEditable={true}
                    type="text"
                    ref={textareaRef}
                    className="chatareatext"
                    onKeyUp={debouncedTyping}
                    name="question"
                    onInput={adjustTextareaHeight}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  {placeHolderVisible && (
                    <div onClick={handlePlaceHolderClick} style={{ position: "absolute", top: "0px", left: "35px" }}>
                      Type your message...
                    </div>
                  )}
                  <Button colorScheme="blue" onClick={handleSubmit} isDisabled={disableSendButton} size="sm" marginRight="10px">
                    <ArrowForwardIcon />
                  </Button>
                </Flex>
              </VStack>
            </Flex>
          </Flex>
        </Box>
      )}

      {!widgetCollapsed && (
        <Box
          position="relative"
          bottom="10px"
          alignSelf="center"
          textAlign="center"
          borderWidth="1px"
          borderRadius="lg"
          padding="2px"
          opacity="0.6"
          display="flex"
          alignItems="center"
        >
          <Text fontSize="xs" color={colorMode === "light" ? "gray.500" : "gray.300"} mr={1}>
            Powered By
          </Text>
          <Box
            borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
            backgroundColor={colorMode === "light" ? "white" : "gray.800"}
            borderRadius="md"
          >
            <Image
              src={Auth?.user?.business?.partner ? "/a-dec-logo.svg" : colorMode === "light" ? "/viva-ai-dark-center-align-2026.svg" : "/viva-ai-light-center-align-2026.svg"}
              alt="Logo"
              width={Auth?.user?.business?.partner ? "60px" : "30px"}
            />
          </Box>
        </Box>
      )}

      {!widgetCollapsed && Auth?.user?.business?.partner && <AdecAIDisclaimer />}
    </Flex>
  );
}

export default InboxMessages;
